export const useAboutPage = () => {
  //
  // Composables
  //
  const { t } = useI18n();
  const { fetchAllServiceVersions, clearCoreCache, loadingMap } = useService("aboutPage.service");

  //
  // State
  //
  const activeTab = useCookie<string>("aboutPage.activeTab", {
    default: () => "overview",
    maxAge: 60 * 60 * 24 * 365, // 1 year
  });

  const servicesData = useState<
    Array<{
      id: string;
      name: string;
      icon: string;
      infoUrl: string;
      healthUrl: string;
      appUrl?: string;
      openApiUrl?: string;
      version: string;
      description: string;
      healthStatus: string;
    }>
  >("aboutPage.servicesData", () => []);

  //
  // Computed
  //
  const tabs = computed(() =>
    [
      ["overview", "mdi-information-outline"],
      // ['services', 'mdi-cloud-outline'],
      // ['actions', 'mdi-lightning-bolt'],
      ["credits", "mdi-account-group-outline"],
      ["copyright", "mdi-copyright"],
    ].map(([value, icon]) => ({
      value,
      icon,
      name: t(`pages.about.tabs.${value}_tab.name`),
    })),
  );

  const credits = computed(() => [
    {
      icon: "mdi-code-braces",
      color: "primary",
      title: t("pages.about.tabs.credits_tab.application_title"),
      authors: "Mikhail Kalatchev, Ivan Kalatchev",
      subtitle: t("pages.about.tabs.credits_tab.authors_of_application"),
    },
    {
      icon: "mdi-nuxt",
      color: "success",
      title: "Nuxt",
      authors: "Daniel Roe, Pooya Parsa",
      subtitle: `Nuxt, MIT ${t("pages.about.tabs.credits_tab.licence")}`,
    },
    {
      icon: "mdi-vuejs",
      color: "success",
      title: "Vue.js",
      authors: "Evan You, Eduardo San Martin Morote",
      subtitle: `VueJS, MIT ${t("pages.about.tabs.credits_tab.licence")}`,
    },
    {
      icon: "mdi-material-design",
      color: "info",
      title: "Vuetify",
      authors: "John Leider, Kael, Andrew Henry",
      subtitle: `Vuetify, MIT ${t("pages.about.tabs.credits_tab.licence")}`,
    },
  ]);
  const creditsData = computed(() =>
    credits.value.map((credit) => ({
      icon: credit.icon,
      iconColor: credit.color,
      authors: credit.authors,
      subtitle: credit.subtitle,
    })),
  );

  const actionsData = computed(() => [
    {
      id: "clear-cache",
      icon: "mdi-refresh",
      iconColor: "error",
      title: t("pages.about.tabs.actions_tab.cache_card.title"),
      description: t("pages.about.tabs.actions_tab.cache_card.content"),
      buttonIcon: "mdi-refresh",
      buttonText: t("pages.about.tabs.actions_tab.cache_card.action"),
      buttonColor: "primary",
      loading: loadingMap.clearCoreCache.value,
    },
  ]);

  //
  // Methods
  //

  /**
   * Clear core cache.
   * @returns {Promise<boolean>} True if successful, false otherwise.
   */
  const refreshCoreCache = async (): Promise<boolean> => {
    loadingMap.clearCoreCache.value = true;

    try {
      const success = await clearCoreCache();
      return success;
    } catch (error) {
      console.error("Error refreshing core cache:", error);
      return false;
    } finally {
      loadingMap.clearCoreCache.value = false;
    }
  };

  /**
   * Fetch all service versions and health status
   */
  const fetchServiceVersions = async (): Promise<boolean> => {
    loadingMap.fetchAllServiceVersions.value = true;

    try {
      servicesData.value = await fetchAllServiceVersions();
      return true;
    } catch (err) {
      console.error("Error fetching service versions:", err);
      return false;
    } finally {
      loadingMap.fetchAllServiceVersions.value = false;
    }
  };

  return {
    // State
    activeTab,
    isCoreCacheRefreshing: readonly(loadingMap.clearCoreCache),
    isServicesRefreshing: readonly(loadingMap.fetchAllServiceVersions),

    // Computed
    tabs: readonly(tabs),
    servicesData: readonly(servicesData),
    actionsData: readonly(actionsData),
    credits: readonly(credits),
    creditsData: readonly(creditsData),

    // Methods
    refreshCoreCache,
    fetchServiceVersions,
  };
};
